import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Avatar } from '../components/ui/Avatar';
import { BoostMark } from '../components/ui/BoostMark';
import { brand } from '../config/brand';
import { DesktopDownload } from '../features/desktop/DesktopDownload';
import { getTheme, setTheme, type Theme } from '../lib/theme';
import { copy } from '../config/copy';
import { BoostsDialog } from '../features/boosts/BoostsDialog';
import { api } from '../lib/api';
import { rooms } from '../features/rooms/api';
import { useLiveHosts } from '../features/rooms/useLiveHosts';
import { useCan, useSession } from '../stores/session';

/**
 * Everything about *you*, behind the avatar.
 *
 * ## Why the header got shorter
 *
 * It was carrying seven controls at once - search, three nav links, a bell, the
 * upload CTA, an admin button, an avatar and a sign-out button - and the three
 * that matter (find something, see what is new, put something up) were
 * competing with account plumbing nobody clicks twice a week.
 *
 * So the bar keeps what people came to do, and the avatar becomes the door to
 * what they occasionally need. Sign out in particular has no business being a
 * permanent button next to the primary action.
 *
 * ## Links, not a `role="menu"`
 *
 * These are navigation, not commands. The ARIA menu pattern obliges arrow-key
 * roving focus and typeahead, and a `role="menu"` without them is a promise to
 * a screen reader that the keyboard does not keep. Plain links inside a panel,
 * with `aria-expanded` on the trigger, is honest and works with Tab.
 */
export function AccountMenu() {
  const user = useSession((state) => state.user);
  const logout = useSession((state) => state.logout);
  const canAdmin = useCan('admin.access');
  const isHost = useCan('host.broadcast');

  /*
   * Whether this host is on air right now.
   *
   * Read from the list the header already has, so this costs no request at all
   * - `useLiveHosts` is fetched on every page for the live count beside the
   * search box, and TanStack hands back the same cache.
   *
   * Deliberately *not* `GET /me/station`: that endpoint creates the station row
   * on read and rolls a finished track over, and a dropdown is the wrong place
   * to do either. Chrome should not have side effects.
   *
   * The one seam: `/live` returns the twenty busiest rooms, so a host with no
   * listeners could fall off the end while twenty others are on. Then this
   * reads "Go live", which is where it was before this existed - it degrades to
   * being unhelpful rather than to being wrong about the destination.
   */
  const { items: onAir } = useLiveHosts();
  const isLive = Boolean(user && onAir.some((room) => room.username === user.username));

  /*
   * Earned boosts, so the count is reachable from any page rather than only
   * from the dashboard. Same query key as the dashboard card, so opening this
   * menu costs nothing that page has not already paid for.
   */
  const { data: boosts } = useQuery({
    queryKey: ['my-boosts'],
    queryFn: () => rooms.boosts(),
    enabled: user !== null,
  });

  /*
   * Whether anything has shipped since this account last looked.
   *
   * The same key the page uses, so opening the menu and then the page is one
   * request rather than two - and the dot clears the moment the page marks
   * itself read, because both read the same cache entry.
   */
  const { data: news } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => api.get<{ unread: number }>('/announcements'),
    enabled: user !== null,
    staleTime: 5 * 60_000,
  });

  const unreadNews = news?.unread ?? 0;

  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [boostsOpen, setBoostsOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Navigating closes it. Without this, clicking a link leaves the panel open
  // over the page it just took you to.
  useEffect(() => setOpen(false), [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;

    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;

      if (panel.current?.contains(target) || trigger.current?.contains(target)) return;

      setOpen(false);
    };

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      setOpen(false);
      // Focus goes back where it came from. Escaping a panel and landing at the
      // top of the document is how keyboard users lose their place.
      trigger.current?.focus();
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const name = user.display_name || user.username;

  return (
    <div className="relative shrink-0">
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={`Account menu for ${name}`}
        data-account-menu-trigger
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-2 rounded-[var(--radius-pill)] border border-line py-1 pl-1 pr-2.5 transition-colors hover:border-line-strong hover:bg-raised"
      >
        <Avatar src={user.avatar} name={name} size={28} />
        {/* The name is hidden on narrow screens, not the avatar: the avatar is
            the target, the name is the label. */}
        <span className="hidden max-w-28 truncate text-sm text-ink sm:block">{name}</span>
        <Chevron open={open} />
      </button>

      {/* Outside the panel, which closes when this opens. */}
      {boostsOpen && <BoostsDialog onClose={() => setBoostsOpen(false)} />}

      {open && (
        <div
          ref={panel}
          data-account-menu
          className="absolute right-0 top-12 z-40 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface shadow-xl"
        >
          <Link
            to={`/${user.username}`}
            className="flex items-center gap-3 border-b border-line px-4 py-4 transition-colors hover:bg-raised"
          >
            <Avatar src={user.avatar} name={name} size={44} />
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-ink">{name}</span>
              <span className="block truncate text-xs text-soft">See your profile</span>
            </span>
          </Link>

          {/*
            The main nav, below the breakpoint where it lives in the bar.
            Without this there is no navigation at all on a phone - the header's
            `lg:flex` simply hides it and nothing takes its place.
          */}
          <div className="border-b border-line py-1 lg:hidden">
            <Item to="/discover" icon={<DiscoverGlyph />}>
              {copy['nav.discover']}
            </Item>
            <Item to="/following" icon={<FeedGlyph />}>
              {copy['nav.following']}
            </Item>
            <Item to="/favorites" icon={<HeartGlyph />}>
              {copy['nav.favorites']}
            </Item>
          </div>

          <div className="py-1">
            {/*
              Always here, and a button rather than a link.

              It used to appear only once somebody held one, which meant the
              progress was invisible to exactly the people who had not worked
              out how to earn it. There is nowhere to *go* either - the answer
              is an explanation, not a page - so it opens a dialog.
            */}
            {boosts && (
              <button
                type="button"
                data-boosts-trigger
                onClick={() => {
                  setOpen(false);
                  setBoostsOpen(true);
                }}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-soft transition-colors hover:bg-raised hover:text-ink"
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-slate ${
                    boosts.available > 0 ? 'text-fresh' : 'text-soft'
                  }`}
                >
                  <BoostMark className="h-4 w-4" />
                </span>

                <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="truncate">{copy['noun.boost.earned.plural']}</span>
                    <span
                      className={`shrink-0 font-mono text-[11px] ${
                        boosts.available > 0 ? 'text-fresh' : 'text-faint'
                      }`}
                    >
                      {boosts.available > 0
                        ? `${boosts.available} ready`
                        : `${boosts.toward_next}/${boosts.threshold}`}
                    </span>
                  </span>

                  {/* Full when they hold one, because the next hundred has not
                      started and a bar sitting at zero would read as a loss. */}
                  <span className="h-1 overflow-hidden rounded-[var(--radius-pill)] bg-track">
                    <span
                      className="block h-full rounded-[var(--radius-pill)] bg-fresh"
                      style={{
                        width:
                          boosts.available > 0
                            ? '100%'
                            : `${Math.min(100, Math.round((boosts.toward_next / boosts.threshold) * 100))}%`,
                      }}
                    />
                  </span>
                </span>
              </button>
            )}

            {/*
              One entry, three labels, because it answers one question - "can I
              go live" - whose honest answer changes.

              Somebody without the role is told hosting exists and sent to the
              application; a link that only appears after approval is a link
              people learn to stop looking for. A host who is off air is invited
              to start. A host who is *already* on air was being invited to go
              live again, which reads as though the site has lost track of them -
              so it says so, and the dot says it before the words do.
            */}
            <Item
              to={isHost ? '/console' : '/host'}
              icon={<BroadcastGlyph />}
              tone={isLive ? 'text-hot-text' : undefined}
              after={
                isLive ? (
                  <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-hot" aria-hidden />
                ) : undefined
              }
            >
              {!isHost ? 'Host a room' : isLive ? "You're on air" : 'Go live'}
            </Item>

            {/*
              Hosting settings, for hosts only.
 
              Points at the same `/host` route the "Host a room" item above
              does - that page is the application before approval and the
              hosting settings after it.
            */}
            {isHost && (
              <Item to="/host" icon={<BroadcastGlyph />}>
                Host settings
              </Item>
            )}

            {/*
              Money, on its own item rather than inside host settings.

              These accounts have no Stripe dashboard, so this is the only place
              a host can see what they have been paid and when the next payout
              lands - which is a thing people check, not a form they fill in
              once. See `EarningsLayout`.
            */}
            {isHost && (
              <Item to="/earnings" icon={<CoinGlyph />}>
                Earnings
              </Item>
            )}

            <Item to="/stats" icon={<StatsGlyph />}>
              Your stats
            </Item>

            {/*
              A dot rather than a count.
              
              "Three things changed" is not a number anybody acts on
              differently from "something changed", and a badge that counts
              release notes reads like unread mail. The dot goes when the page
              is opened.
            */}
            <Item
              to="/whats-new"
              icon={<SparkGlyph />}
              after={
                unreadNews > 0 ? (
                  <span
                    aria-label={`${unreadNews} unread`}
                    data-whats-new-dot
                    className="h-2 w-2 shrink-0 rounded-full bg-signal"
                  />
                ) : undefined
              }
            >
              What's new
            </Item>

            <Item to="/settings" icon={<GearGlyph />}>
              Settings
            </Item>

            {canAdmin && (
              <Item to="/admin" icon={<ShieldGlyph />}>
                Admin
              </Item>
            )}
          </div>

          {/*
            The desktop app, in the same section as Appearance and for the same
            reason: both belong to this machine rather than to the account. It
            renders nothing inside the desktop app itself - see
            `DesktopDownload`.
          */}
          <DesktopDownload variant="menu" />

          {/*
            Appearance, above sign out.

            A device preference rather than an account one, so it lives here
            with the other things that belong to this browser rather than on the
            settings page with the things that belong to the person. It also
            wants to be one click from anywhere, which a settings page is not.
          */}
          <div className="border-t border-line px-4 py-3">
            <span className="meta-caps mb-2 block text-faint">Appearance</span>
            <ThemeChoice />
          </div>

          <div className="border-t border-line py-1">
            <button
              type="button"
              data-sign-out
              onClick={async () => {
                setOpen(false);
                await logout();
                navigate('/login');
              }}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-soft transition-colors hover:bg-raised hover:text-ink"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-slate text-soft">
                <ExitGlyph />
              </span>
              Sign out
            </button>
          </div>

          <p className="border-t border-line px-4 py-3 text-xs text-faint">
            © {new Date().getFullYear()} {brand.name}
          </p>
        </div>
      )}
    </div>
  );
}

function Item({
  to,
  icon,
  children,
  tone,
  after,
}: {
  to: string;
  icon: ReactNode;
  children: ReactNode;
  /** Overrides the resting colour, for an item that is reporting a state. */
  tone?: string;
  /** Pinned to the right of the row - a status dot, not a second action. */
  after?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-raised hover:text-ink ${
        tone ?? 'text-soft'
      }`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-slate text-soft">
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {after}
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Glyphs                                                                      */
/*                                                                             */
/* Inline rather than an icon package: there are seven of them, they are all    */
/* one path, and a dependency for that is a dependency to keep updated.        */
/* -------------------------------------------------------------------------- */

/** Three bars of different heights - a chart, not a speedometer. */
function CoinGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.4">
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.6v6.8M9.9 6.2a2 2 0 0 0-3.6 1c0 1.7 3.4 1 3.4 2.6a2 2 0 0 1-3.6 1" strokeLinecap="round" />
    </svg>
  );
}

function StatsGlyph() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M3.5 12.5v-3M8 12.5v-6M12.5 12.5v-9" />
    </svg>
  );
}

/** The earned boost: an upward chevron over a bar, not a generic star. */
const stroke = {
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className: 'h-4 w-4',
  'aria-hidden': true,
};

function SparkGlyph() {
  return (
    <svg {...stroke} className="h-4 w-4">
      <path d="M8 2.5l1.6 3.9 3.9 1.6-3.9 1.6L8 13.5l-1.6-3.9L2.5 8l3.9-1.6z" />
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      {...stroke}
      className={`h-3.5 w-3.5 shrink-0 text-faint transition-transform ${open ? 'rotate-180' : ''}`}
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}

function DiscoverGlyph() {
  return (
    <svg {...stroke}>
      <circle cx="8" cy="8" r="6" />
      <path d="M10.5 5.5L9 9l-3.5 1.5L7 7z" />
    </svg>
  );
}

function FeedGlyph() {
  return (
    <svg {...stroke}>
      <path d="M3 4.5h10M3 8h10M3 11.5h6" />
    </svg>
  );
}

function HeartGlyph() {
  return (
    <svg {...stroke}>
      <path d="M8 13.5s-5-3.3-5-6.5a2.9 2.9 0 0 1 5-1.9 2.9 2.9 0 0 1 5 1.9c0 3.2-5 6.5-5 6.5Z" />
    </svg>
  );
}

function BroadcastGlyph() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-[18px] w-[18px]"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="2" fill="currentColor" stroke="none" />
      <path d="M6.8 6.8a4.5 4.5 0 0 0 0 6.4M13.2 13.2a4.5 4.5 0 0 0 0-6.4" />
      <path d="M4.3 4.3a8 8 0 0 0 0 11.4M15.7 15.7a8 8 0 0 0 0-11.4" />
    </svg>
  );
}

function GearGlyph() {
  return (
    <svg {...stroke}>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.8v1.6M8 12.6v1.6M14.2 8h-1.6M3.4 8H1.8M12.4 3.6l-1.1 1.1M4.7 11.3l-1.1 1.1M12.4 12.4l-1.1-1.1M4.7 4.7L3.6 3.6" />
    </svg>
  );
}

function ShieldGlyph() {
  return (
    <svg {...stroke}>
      <path d="M8 1.8l5 2v4.4c0 3-2.2 5-5 6-2.8-1-5-3-5-6V3.8z" />
    </svg>
  );
}

function ExitGlyph() {
  return (
    <svg {...stroke}>
      <path d="M6 2.5H3.5v11H6M10 5l3 3-3 3M13 8H6.5" />
    </svg>
  );
}

/**
 * System, light, dark - as a segmented control rather than a switch.
 *
 * A two-state toggle cannot express "follow my device", which is the state most
 * people are in and the only one that keeps working when their device changes
 * at sunset. Three buttons say what the three states are; a switch would have
 * to pick one of them silently and be wrong for everybody else.
 *
 * Selection reads from `getTheme` on every render rather than being held in
 * state, because the attribute on `<html>` is the truth and a copy of it here
 * would be a second place to get out of step.
 */
function ThemeChoice() {
  const [, force] = useState(0);
  const current = getTheme();

  const options: Array<{ value: Theme; label: string }> = [
    { value: 'system', label: 'System' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Appearance"
      data-theme-choice
      className="flex items-center gap-1 rounded-[var(--radius-pill)] border border-line p-1"
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={current === option.value}
          data-theme-option={option.value}
          onClick={() => {
            setTheme(option.value);
            // The attribute changed, not any state this component owns; a
            // nudge is what redraws the selected ring.
            force((n) => n + 1);
          }}
          className={`flex-1 rounded-[var(--radius-pill)] px-2 py-1 text-[12px] font-medium transition-colors ${
            current === option.value
              ? 'bg-accent text-accent-ink'
              : 'text-soft hover:bg-raised hover:text-ink'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
